import { TypeAheadItem } from "../../controls/ControlTypeAhead";
import { EditorDefns, IRequestStatus } from "../../utility";
import {
  Analysis,
  AnalysisId,
  AnalysisMethod,
  AnalysisMethodId,
  AnalysisSet,
  AnalysisSetId,
  CaseWhenCondition,
  ConditionComparatorEnum,
  DataSubset,
  DataSubsetId,
  GroupingFactor,
  GroupingFactorId,
  WhereClause,
} from "../ars_ldm";

/**
 * Conditional displayValues
 */
export interface ConditionalDisplayValue<T = string> {
  /** The condition to evaluate for displaying the value */
  condition: WhereClause;
  /** The value to display when the condition is met */
  displayValue: T;
}

/**
 * Represents a variable available for use in where clause conditions.
 * Extends TypeAheadItem to support typeahead functionality.
 */
export interface AnnotationVariable extends TypeAheadItem {
  /** The dataset and variable name combined, e.g., "ADSL.AGE" */
  value: string;
  /** Unique identifier for the variable */
  oid: string;
  /** Name of the variable */
  name: string;
  /** Data type of the variable (e.g., "text", "numeric") */
  dataType: string;
  /** Maximum length for the variable's value */
  length: number;
  /** Description/label of the variable */
  description: string;
  /** Dataset containing the variable (e.g., "ADSL") */
  dataset: string;
  /** Code list identifier if the variable uses controlled terminology */
  codeList?: string | number;
}

/**
 * Single update for PATCH_ARS_STATE operation.
 */
export interface ArsStateUpdate {
  /** JSON Pointer path to the data in ArsContext state */
  dataPath: string;
  /** Value to set at the pointer location */
  value: unknown;
}

/**
 * Core ARS data shared with the application through updateARS.
 */
export interface ArsDataState<T = string> {
  // Read-only reference data (loaded from backend/hierarchy)
  /** List of variables available for use in where clause conditions */
  variables: AnnotationVariable[];
  /** Available comparators for where clause conditions */
  comparators: ConditionComparatorEnum[];

  // Editable/sharable lists (persisted to backend)
  /** Analysis sets indexed by ID */
  analysisSets: Record<AnalysisSetId, AnalysisSet>;
  /** Analyses indexed by ID */
  analyses: Record<AnalysisId, Analysis>;
  /** Case when conditions indexed by ID */
  caseWhenConditions: Record<string, CaseWhenCondition<T>>;
  /** Data subsets indexed by ID */
  dataSubsets: Record<DataSubsetId, DataSubset>;
  /** Analysis methods indexed by ID */
  analysisMethods: Record<AnalysisMethodId, AnalysisMethod>;
  /** Grouping factors indexed by ID */
  groupingFactors: Record<GroupingFactorId, GroupingFactor>;

  // UI state - for loading/error status
  /** Request status for UI operations */
  changed: boolean;
  /** Optional flag to disable Analysis Method editing */
  disableAnalysisMethodEdit: boolean;
  /** Editor definitions for the ARS context */
  editorDefns: EditorDefns<T>;
  /** Current load status for UI operations */
  uiRequestStatus: IRequestStatus<string | object>;
  /** Set of variable names currently in use in the ARS context */
  datasetsInUse: Set<string>;
  variablesInUse: Set<string>;
}

export type ArsSidebarView = "groupingFactor" | "operation" | "variable";

/**
 * Represents current sidebar state
 */
export interface ArsSidebarState {
  /** Selected analysis method(s) */
  analysisMethodId?: string;
  /** Selected dataset */
  dataset?: string;
  /** Text filter for sidebar items */
  filter: string;
  /** Current view in the sidebar */
  view: ArsSidebarView;
}

/**
 * State shape for the ARS context.
 */
export interface ArsContextState<T = string>
  extends
    Record<
      string,
      | boolean
      | number
      | AnnotationVariable[]
      | ConditionComparatorEnum[]
      | Record<
          string,
          | Analysis
          | AnalysisMethod
          | AnalysisSet
          | CaseWhenCondition<T>
          | DataSubset
          | GroupingFactor
        >
      | IRequestStatus<string | object>
      | ArsSidebarState
      | EditorDefns<T>
      | Set<string>
    >,
    ArsDataState<T> {
  // Sidebar state
  currentSidebar: ArsSidebarState;
}

/**
 * Context interface combining state and dispatch.
 */
export interface IArsContext<T = string> {
  /** Current ARS state */
  state: ArsContextState<T>;
  /** Dispatch function for ARS actions */
  dispatch: React.Dispatch<IArsAction>;
}

/**
 * Magic name for current hierarchy id.
 */
export const CURRENT_HIERARCHY_ID = "__current__";

// Action constants
export const ADD_EXTRA_WHERE_CLAUSE = "ADD_EXTRA_WHERE_CLAUSE";
export const ADD_NOT_TO_WHERE_CLAUSE = "ADD_NOT_TO_WHERE_CLAUSE";
export const ARS_CHANGE_SENT = "ARS_CHANGE_SENT";
export const LOAD_ARS_PATCH = "LOAD_ARS_PATCH";
export const PATCH_ARS_STATE = "PATCH_ARS_STATE";
export const REMOVE_ARS_ITEM = "REMOVE_ARS_ITEM";
export const REMOVE_NOT_FROM_WHERE_CLAUSE = "REMOVE_NOT_FROM_WHERE_CLAUSE";
export const REMOVE_WHERE_CLAUSE = "REMOVE_WHERE_CLAUSE";
export const RESET_STATE = "RESET_STATE";
export const SET_SIDEBAR_ANALYSIS_METHOD_ID = "SET_SIDEBAR_ANALYSIS_METHOD_ID";
export const SET_SIDEBAR_DATASET = "SET_SIDEBAR_DATASET";
export const SET_SIDEBAR_FILTER = "SET_SIDEBAR_FILTER";
export const SET_SIDEBAR_VIEW = "SET_SIDEBAR_VIEW";
export const SET_UI_REQUEST_STATUS = "SET_UI_REQUEST_STATUS";
export const SET_VARIABLES_IN_USE = "SET_VARIABLES_IN_USE";

/**
 * Union type of all action operation strings.
 */
export type ArsOperation =
  | typeof ADD_EXTRA_WHERE_CLAUSE
  | typeof ADD_NOT_TO_WHERE_CLAUSE
  | typeof ARS_CHANGE_SENT
  | typeof LOAD_ARS_PATCH
  | typeof PATCH_ARS_STATE
  | typeof REMOVE_ARS_ITEM
  | typeof REMOVE_NOT_FROM_WHERE_CLAUSE
  | typeof REMOVE_WHERE_CLAUSE
  | typeof RESET_STATE
  | typeof SET_SIDEBAR_ANALYSIS_METHOD_ID
  | typeof SET_SIDEBAR_DATASET
  | typeof SET_SIDEBAR_FILTER
  | typeof SET_SIDEBAR_VIEW
  | typeof SET_UI_REQUEST_STATUS
  | typeof SET_VARIABLES_IN_USE;

/**
 * Base interface for all ARS actions.
 */
export interface IArsActionBase {
  operation: ArsOperation;
}

export interface IArsActionAddExtraWhereClause extends IArsActionBase {
  operation: typeof ADD_EXTRA_WHERE_CLAUSE;
  /** Data path of the entity containing the condition or compoundExpression */
  dataPath: string;
  newLength: number;
}

export interface IArsActionNotWhereClause extends IArsActionBase {
  operation: typeof ADD_NOT_TO_WHERE_CLAUSE;
  /** Data path of the where clause to negate */
  dataPath: string;
}

export interface IArsActionChangeSent extends IArsActionBase {
  operation: typeof ARS_CHANGE_SENT;
}

export interface IArsActionLoadArsPatch extends IArsActionBase {
  operation: typeof LOAD_ARS_PATCH;
  /** Array of synced updates to apply without creating undo history */
  updates: ArsStateUpdate[];
}

export interface IArsActionPatchArsStateMultiple extends IArsActionBase {
  operation: typeof PATCH_ARS_STATE;
  /** Array of updates to apply atomically */
  updates: ArsStateUpdate[];
}
export interface IArsActionPatchArsStateSingle extends IArsActionBase, ArsStateUpdate {
  operation: typeof PATCH_ARS_STATE;
}

export interface IArsActionRemoveItem extends IArsActionBase {
  operation: typeof REMOVE_ARS_ITEM;
  dataPath: string;
  newLength?: number;
}
export interface IArsActionRemoveNotFromWhereClause extends IArsActionBase {
  operation: typeof REMOVE_NOT_FROM_WHERE_CLAUSE;
  /** Data path of the where clause to remove NOT from */
  dataPath: string;
}
export interface IArsActionRemoveWhereClause extends IArsActionBase {
  operation: typeof REMOVE_WHERE_CLAUSE;
  /** Data path of the entity containing the compoundExpression */
  dataPath: string;
  /** The id of the SubClause to remove */
  id: string;
}

export interface IArsActionSetSidebarAnalysisMethodId extends IArsActionBase {
  operation: typeof SET_SIDEBAR_ANALYSIS_METHOD_ID;
  analysisMethodId: string | undefined;
}

export interface IArsActionSetSidebarDataset extends IArsActionBase {
  operation: typeof SET_SIDEBAR_DATASET;
  dataset: string;
}

export interface IArsActionSetSidebarFilter extends IArsActionBase {
  operation: typeof SET_SIDEBAR_FILTER;
  filter: string;
}

export interface IArsActionSetSidebarView extends IArsActionBase {
  operation: typeof SET_SIDEBAR_VIEW;
  view: ArsSidebarView;
}

export interface IArsActionSetUIRequestStatus extends IArsActionBase {
  operation: typeof SET_UI_REQUEST_STATUS;
  uiRequestStatus: IRequestStatus<string | object>;
}

export interface IArsActionSetVariablesInUse extends IArsActionBase {
  operation: typeof SET_VARIABLES_IN_USE;
  variablesInUse: Set<string>;
}

/**
 * Union type of all possible ARS actions.
 */
export type IArsAction =
  | IArsActionAddExtraWhereClause
  | IArsActionChangeSent
  | IArsActionLoadArsPatch
  | IArsActionNotWhereClause
  | IArsActionPatchArsStateMultiple
  | IArsActionPatchArsStateSingle
  | IArsActionRemoveItem
  | IArsActionRemoveNotFromWhereClause
  | IArsActionRemoveWhereClause
  | IArsActionSetSidebarAnalysisMethodId
  | IArsActionSetSidebarDataset
  | IArsActionSetSidebarFilter
  | IArsActionSetSidebarView
  | IArsActionSetUIRequestStatus
  | IArsActionSetVariablesInUse;
